import {AccordionSummary, Divider, Stack, Typography, useTheme} from '@mui/material';
import React from 'react';

import {DataInfo} from 'presentation/components';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';

import {useMediaQueries} from 'presentation/pages/private/hooks';
import {truncateText} from 'presentation/utils';
import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {HeaderSection} from '../HeaderSection';
import {StyledAccordion, StyledAccordionDetails, StyledBox, StyledStack} from './styles';
import {RequestInfoProps} from './types';

const {
	requestRegistered,
	attachment,
	providerData,
	patientData,
	fiscalDocument,
	withNFe,
	withoutNFe,
} = translations['pt-br'].privateFlow.refund.holderDataPage.requestInfo;

export const RequestInfo = ({
	patientName,
	doctorName,
	date,
	numFiscalDocument,
	numFiscalDigit,
	value,
	hasNfe,
	requestNumber,
	city,
	state,
	isSingleItem,
	attachments,
	specialtyName,
	providerName,
	handleClickProviderData,
	handleClickPatientData,
	handleClickFiscalDocument,
	handleClickAttachment,
}: RequestInfoProps) => {
	const theme = useTheme();
	const {isMobile} = useMediaQueries();

	return (
		<StyledAccordion defaultExpanded={!!isSingleItem} disableGutters>
			<AccordionSummary
				expandIcon={
					<Icon name="expand-more-filled" color={theme.palette.primary.main} size={32} />
				}
				aria-controls="panel1-content"
				id="panel1-header">
				<Stack gap={'4px'} borderRadius={1}>
					<Typography variant="subtitle2" color="text.secondary">
						{requestNumber ? `${requestNumber}ª ` : ''} {requestRegistered}
					</Typography>
				</Stack>
			</AccordionSummary>

			<StyledAccordionDetails>
				<Divider />
				<StyledBox>
					<StyledStack>
						<HeaderSection
							dataTagId={RefundTaggingEnum.CLICK_ALTERAR_DADOS_PRESTADOR}
							title={providerData}
							handleClick={handleClickProviderData}
						/>
						<DataInfo
							primaryText={doctorName}
							iconName="doctor-filled"
							listItemProps={{sx: {p: 0}}}
						/>
						{providerName ? (
							<DataInfo
								primaryText={providerName}
								iconName="group-filled"
								listItemProps={{sx: {p: 0}}}
							/>
						) : null}
					</StyledStack>
					<Divider />

					<StyledStack>
						<HeaderSection
							dataTagId={RefundTaggingEnum.CLICK_ALTERAR_DADOS_PACIENTE}
							title={patientData}
							handleClick={handleClickPatientData}
						/>
						<DataInfo
							primaryText={patientName}
							iconName="person-filled"
							listItemProps={{sx: {p: 0}}}
						/>
						<DataInfo
							primaryText={date}
							iconName="event-filled"
							listItemProps={{sx: {p: 0}}}
						/>
						<DataInfo
							primaryText={specialtyName}
							iconName="stethoscope-default"
							listItemProps={{sx: {p: 0}}}
						/>
					</StyledStack>
					<Divider />

					<StyledStack>
						<HeaderSection
							dataTagId={RefundTaggingEnum.CLICK_ALTERAR_DOCUMENTO_FISCAL}
							title={fiscalDocument}
							handleClick={handleClickFiscalDocument}
						/>
						<DataInfo
							primaryText={`Nº${numFiscalDocument}`}
							iconName="insert-drive-file-filled"
							listItemProps={{sx: {p: 0}}}
						/>
						<DataInfo
							primaryText={value}
							iconName="receipt-long-filled"
							listItemProps={{sx: {p: 0}}}
						/>
						{numFiscalDigit ? (
							<DataInfo
								primaryText={numFiscalDigit}
								iconName="tag-filled"
								listItemProps={{sx: {p: 0}}}
							/>
						) : null}
						<DataInfo
							primaryText={hasNfe ? withNFe : withoutNFe}
							iconName="notes-filled"
							listItemProps={{sx: {p: 0}}}
						/>
						{state && city ? (
							<DataInfo
								primaryText={`${state} - ${city}`}
								iconName="location-on-filled"
								listItemProps={{sx: {p: 0}}}
							/>
						) : null}
					</StyledStack>
					<Divider />

					{attachments ? (
						<StyledStack>
							<HeaderSection
								dataTagId={RefundTaggingEnum.CLICK_ALTERAR_ANEXOS}
								title={attachment}
								handleClick={handleClickAttachment}
							/>
							<Stack>
								{attachments.map(file => (
									<DataInfo
										key={file.id}
										primaryText={
											isMobile
												? truncateText({
														text: file.filename,
														containerWidth: 260,
														targetWidth: 300,
														txtWidth: 260,
														shrinkFactor: 2,
													})
												: file.filename
										}
										iconName="upload-file-filled"
										listItemProps={{sx: {p: 0}}}
									/>
								))}
							</Stack>
						</StyledStack>
					) : null}
				</StyledBox>
			</StyledAccordionDetails>
		</StyledAccordion>
	);
};
